import React from "react";
import { motion } from "framer-motion";
import { Crown, Compass, ClipboardList, User, Users } from "lucide-react";
import { IMAGES } from "../lib/images";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import FullImage from "../components/site/FullImage";

const GROUP_ICONS = {
  Leaders: Crown,
  Directors: Compass,
  "Organizing Secretaries": ClipboardList,
};
const iconFor = (group) => GROUP_ICONS[group] || Users;

const GROUP_BLURBS = {
  Leaders: "Setting the vision and guiding the mission of ÀsÀNigHT Worldwide.",
  Directors: "Driving strategy, programmes, and cultural innovation across our centres.",
  "Organizing Secretaries": "Coordinating events, community, and the day-to-day life of the organization.",
};

function PersonCard({ person, role }) {
  return (
    <div
      className="group border border-parchment rounded-2xl overflow-hidden bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300"
      data-testid={`leader-card-${role.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      {person.image ? (
        <FullImage src={person.image} alt={person.name} className="aspect-[4/5]" imgClassName="group-hover:scale-105 transition-transform duration-700" />
      ) : (
        <div className="relative aspect-[4/5] overflow-hidden bg-forest-deep grain-overlay grid place-items-center">
          <div className="text-center px-6">
            <span className="grid place-items-center w-20 h-20 rounded-full border border-gold/40 text-gold mx-auto mb-4">
              <User className="w-9 h-9" />
            </span>
            <p className="text-xs uppercase tracking-[0.2em] text-gold-soft">Photo Coming Soon</p>
          </div>
        </div>
      )}
      <div className="p-6 text-center">
        <h3 className="font-serif text-2xl text-ink">{person.name}</h3>
        {person.title && <p className="text-sm text-gold mt-1">{person.title}</p>}
        {person.bio && <p className="text-sm text-ink-muted mt-3 leading-relaxed">{person.bio}</p>}
      </div>
    </div>
  );
}

export default function Leadership() {
  useSEO(
    "Visionary Leadership",
    "Meet the visionary leaders of ÀsÀNigHT Worldwide — pioneering cultural innovation, strategic excellence, and global Yorùbá advancement."
  );

  const [leaders, setLeaders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [banner, setBanner] = React.useState(IMAGES.headdressShield);

  React.useEffect(() => {
    api
      .get("/leaders")
      .then((r) => setLeaders(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/site-settings").then((r) => { if (r.data?.banner_leadership) setBanner(r.data.banner_leadership); }).catch(() => {});
  }, []);

  const groups = React.useMemo(() => {
    const order = [];
    const map = {};
    for (const p of leaders) {
      const g = p.group || "Team";
      if (!map[g]) { map[g] = []; order.push(g); }
      map[g].push(p);
    }
    return order.map((g) => ({ role: g, people: map[g] }));
  }, [leaders]);

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[56vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={banner} alt="ÀsÀNigHT leadership" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/75 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Our Team</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero"
          >
            Visionary Leadership
          </motion.h1>
          <p className="mt-5 text-cream/80 text-lg max-w-2xl">
            Pioneering leaders driving cultural innovation, strategic excellence, and global
            Yorùbá advancement.
          </p>
        </div>
      </header>

      {/* GROUPS */}
      <div className="max-w-6xl mx-auto px-6 py-20 space-y-20">
        {loading ? (
          <p className="text-ink-muted text-center py-16">Loading…</p>
        ) : groups.length === 0 ? (
          <Reveal>
            <div className="text-center border border-dashed border-parchment rounded-2xl py-12 px-6 bg-sand" data-testid="leadership-empty">
              <p className="font-serif text-2xl text-ink mb-2">Profiles Being Prepared</p>
              <p className="text-ink-muted max-w-xl mx-auto">
                Photographs and full biographies of our leaders, directors, and organizing
                secretaries will be unveiled here soon.
              </p>
            </div>
          </Reveal>
        ) : (
          groups.map((group) => {
            const Icon = iconFor(group.role);
            return (
              <section key={group.role} data-testid={`leadership-group-${group.role.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <Reveal>
                  <div className="flex items-center gap-4 mb-3">
                    <span className="grid place-items-center w-12 h-12 rounded-full bg-forest text-cream shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <h2 className="font-serif text-3xl md:text-4xl text-ink">{group.role}</h2>
                  </div>
                  {GROUP_BLURBS[group.role] && <p className="text-ink-muted mb-10 max-w-2xl">{GROUP_BLURBS[group.role]}</p>}
                </Reveal>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {group.people.map((p, i) => (
                    <Reveal key={p.id || i} delay={i * 0.08}>
                      <PersonCard person={p} role={`${group.role}-${p.id || i}`} />
                    </Reveal>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>

      <Newsletter />
    </div>
  );
}
